import { Payment, useBill, usePayment, useVendor } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import React, { ComponentProps } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { NewSinglePaymentStepLayout } from '../../NewSinglePaymentStepLayout';
import { PaymentScheduledSuccessfullyActivity } from '../../PaymentScheduled';
import { PaymentFlowDoneAction } from '../../types';
import { PaymentFlowActivity } from '../PaymentFlowActivity/PaymentFlowActivity';
import { PaymentFlowAction } from '../PaymentFlowActivity/PaymentFlowForm/PaymentFlowForm';
import { CreatePaymentFromBillParamsType, PaymentFlowFormFields } from '../types';

type CreatePaymentActivityProps = {
  billId?: string;
  vendorId?: string;
  deliveryMethodId?: string;
  amountToPay?: string;
  onClose: VoidFunction;
  onDone: (id: Payment['id'], action: PaymentFlowDoneAction, isPendingApproval: boolean) => void;
};

export const CreatePaymentActivity = ({
  billId,
  vendorId,
  deliveryMethodId,
  amountToPay,
  onClose,
  onDone,
}: CreatePaymentActivityProps) => {
  const navigate = useNavigate();
  const [addDeliveryDateToCreateAndUpdate] = useFeature<boolean>(
    FeatureFlags.AddDeliveryDateToCreateAndUpdatePayment,
    false
  );

  const {
    data: newPayment,
    create: createPayment,
    isMutating: isCreatingPayment,
  } = usePayment({ enabled: false, id: 'CreatePaymentActivityPaymentId' });

  const createPaymentFromBill = (data: CreatePaymentFromBillParamsType) =>
    createPayment({
      paymentBillsInfo: [
        {
          id: data.billId,
          paymentAmount: Number(data.amountToPay),
        },
      ],
      vendorId: data.vendorId,
      deliveryMethodId: data.deliveryMethodId,
      fundingSourceId: data.fundingSourceId,
      scheduledDate: data.scheduleDate.toISOString(),
      deliveryDate: addDeliveryDateToCreateAndUpdate ? data?.deliveryDate?.toISOString() : undefined,
      deliveryPreference: data.deliveryPreference,
      ...(data.noteToVendor && { noteToVendor: data.noteToVendor }),
    });

  const onSubmit = ({
    vendorId,
    deliveryMethodId,
    fundingSourceId,
    deliveryDate,
    scheduleDate,
    amountToPay,
    deliveryPreference,
    noteToVendor,
  }: PaymentFlowFormFields) => {
    if (
      vendorId &&
      deliveryMethodId &&
      fundingSourceId &&
      deliveryDate &&
      scheduleDate &&
      amountToPay &&
      deliveryPreference
    ) {
      if (billId) {
        return createPaymentFromBill({
          billId,
          vendorId,
          deliveryMethodId,
          fundingSourceId,
          deliveryDate,
          scheduleDate,
          amountToPay,
          deliveryPreference,
          noteToVendor,
        }).then(() => {
          navigate('success');
        });
      }
    }

    throw new Error('cannot create payment');
  };

  const { data: bill, isLoading: isBillLoading } = useBill({
    enabled: Boolean(billId),
    id: billId,
  });

  const defaultVendorId = bill?.vendorId || vendorId;

  const { data: vendor, isLoading: isVendorLoading } = useVendor({
    enabled: Boolean(defaultVendorId),
    id: defaultVendorId,
  });

  const billDefaults: ComponentProps<typeof PaymentFlowActivity>['defaultValues'] = {
    vendorId: defaultVendorId,
    deliveryMethodId,
    amountToPay: bill?.balance.toString() || amountToPay,
    deliveryDate: bill?.dueDate,
    vendorEmail: vendor?.contact.email,
  };

  if (isBillLoading || isVendorLoading) {
    return <NewSinglePaymentStepLayout isLoading />;
  }

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <PaymentFlowActivity
            defaultValues={billDefaults}
            billId={billId}
            paymentFlowAction={PaymentFlowAction.CreatePayment}
            isSubmitting={isCreatingPayment}
            onSubmit={onSubmit}
            onClose={onClose}
          />
        }
      />
      <Route
        path="/success"
        element={
          newPayment && (
            <PaymentScheduledSuccessfullyActivity
              paymentsIds={[newPayment.id]}
              onClose={onClose}
              onDone={(action: PaymentFlowDoneAction) => onDone(newPayment.id, action, false)}
            />
          )
        }
      />
    </Routes>
  );
};
